import { z } from 'zod';

/**
 * Every message the backend sends over the realtime SSE stream, as one
 * discriminated union — the single source of truth for the wire. The consumer
 * `safeParse`s every frame against it and `applyServerEvent` switches on `type`
 * exhaustively, so nothing crosses the wire that a client doesn't explicitly
 * handle. Transient data — pub/sub is fire-and-forget, lossy, nothing persisted
 * — so it is a ValidationSchema.
 *
 * Most variants are thin: a `type` plus only the ids needed to choose which
 * cache keys to invalidate (the client then refetches the authoritative data; we
 * don't put entity data on the wire). `platform.version` is the exception — see
 * its note below.
 *
 * Add a variant (and a matching `applyServerEvent` case — the switch won't
 * compile until you do) as more queries adopt the cache key factory.
 */
const TransactionUpdatedValidationSchema = z.object({
  type: z.literal('transaction.updated'),
  dealershipId: z.string(),
  transactionId: z.string()
});

/**
 * Periodic announcement of the backend's platform version. Unlike the thin
 * change-events it carries a value, and `applyServerEvent` applies it with a
 * direct cache write (no refetch) so clients can prompt a refresh after a deploy
 * without polling. It also doubles as the stream keep-alive — the backend emits
 * it on a fixed cadence — and carrying the value is safe because it is an
 * idempotent, periodic full-state signal rather than a delta.
 */
const PlatformVersionValidationSchema = z.object({
  type: z.literal('platform.version'),
  platformVersion: z.string()
});

export const ServerEventValidationSchema = z.discriminatedUnion('type', [
  TransactionUpdatedValidationSchema,
  PlatformVersionValidationSchema
]);

export type ServerEventValidationSchema = z.TypeOf<
  typeof ServerEventValidationSchema
>;
